import { useAdOperatorAvailability } from '@backoffice-frontend/ad-operator-availability'
import { AdWaypointPassengers } from '@backoffice-frontend/ad-passenger-list'
import {
  getAlertIconComponent,
  getAlertSeverity,
  getBackgroundColor,
  getBorderColor,
  getForegroundTextColor,
  useTaskTypeTranslation,
} from '@backoffice-frontend/alerts-common'
import { INFO_NOT_AVAILABLE_NA } from '@backoffice-frontend/common'
import {
  FccAdAlertType,
  FccOperatorAvailabilityType,
} from '@backoffice-frontend/graphql'
import { useCountUpSince } from '@backoffice-frontend/hooks'
import { useUserHasAccess } from '@backoffice-frontend/restricted'
import type { Theme } from '@emotion/react'
import { BoumRight } from '@moia-dev/moia-token-claims'
import { HeadsetIcon } from '@moia-dev/pace-icons'
import { Card, Icon, Text } from '@moia-dev/pace-web'
import type { ReactNode } from 'react'
import { TakeOverButton } from '../TakeOverButton'
import type {
  AdAlertListCardAlertFragment,
  AdAlertListCardVehicleFragment,
} from './AdAlertListCard.hook'

type AdAlertListCardProps = {
  vehicle: AdAlertListCardVehicleFragment
  disableAction?: boolean
  disableWaypointPassengers?: boolean
}

export const AdAlertListCard = ({
  vehicle,
  disableAction = false,
  disableWaypointPassengers = false,
}: AdAlertListCardProps) => {
  const hasAccessToEmployeeData = useUserHasAccess({
    requiredRights: [BoumRight.EMPLOYEE_R],
  })

  const { availability } = useAdOperatorAvailability()

  const alerts = vehicle.fccCurrentAdAlerts?.alerts ?? []

  if (alerts.length === 0) {
    return null
  }

  const highestSeverityAlert = alerts[0]

  const hasWaypointAttendance = alerts.some(
    alert => alert.type === FccAdAlertType.WaypointAttendance,
  )

  const upcomingWaypoint = vehicle.fccUpcomingWaypoints?.at(0)
  const futureWaypoints = vehicle.fccUpcomingWaypoints?.slice(1)

  const displayPassengerInfo =
    !disableWaypointPassengers &&
    hasWaypointAttendance &&
    upcomingWaypoint &&
    futureWaypoints

  const handledBy = (() => {
    if (!vehicle.fccHandledByOperator) {
      return undefined
    }

    return hasAccessToEmployeeData
      ? `${vehicle.fccHandledByOperator.firstName} ${vehicle.fccHandledByOperator.lastName.charAt(0)}.`
      : INFO_NOT_AVAILABLE_NA
  })()

  return (
    <Card
      padding="none"
      css={theme => ({
        border: `1px solid ${getBorderColor(highestSeverityAlert, theme)}`,
        borderTopWidth: '8px',
      })}
    >
      <div
        css={theme => ({
          display: 'flex',
          alignItems: alerts.length === 1 ? 'center' : 'flex-end',
          padding: theme.space.Space4,
          paddingBottom: displayPassengerInfo ? 0 : theme.space.Space4,
        })}
      >
        <div
          css={theme => ({
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: theme.space.Space2,
          })}
        >
          {alerts.map(alert => (
            <>
              <Alert key={alert.id} alert={alert} />
            </>
          ))}
        </div>
        {!disableAction &&
          availability === FccOperatorAvailabilityType.Available &&
          (handledBy ? (
            <div
              css={theme => ({
                display: 'flex',
                alignItems: 'center',
                gap: theme.space.Space1,
              })}
            >
              <Icon size={16}>
                <HeadsetIcon
                  css={theme => ({ color: theme.semantic.ColorContentReduced })}
                />
              </Icon>
              <Text
                css={theme => ({
                  fontSize: 12,
                  color: theme.semantic.ColorContentReduced,
                })}
              >
                {handledBy}
              </Text>
            </div>
          ) : (
            <TakeOverButton vehicleId={vehicle.id} size="compact" />
          ))}
      </div>
      {displayPassengerInfo && (
        <AdWaypointPassengers
          css={theme => ({
            marginTop: theme.space.Space4,
            borderTop: `1px solid ${theme.semantic.ColorAccessoryReduced}`,
          })}
          waypoint={upcomingWaypoint}
          futureWaypoints={futureWaypoints}
        />
      )}
    </Card>
  )
}

const getAlertIcon = (alert: AdAlertListCardAlertFragment): ReactNode => {
  const severity = getAlertSeverity(alert)
  const IconComponent = getAlertIconComponent(severity)

  return (
    <Icon
      size={24}
      css={theme => ({
        backgroundColor: getBackgroundColor(alert, theme),
        borderRadius: 4,
      })}
    >
      <IconComponent
        css={(theme: Theme) => ({
          color: getForegroundTextColor(alert, theme),
        })}
      />
    </Icon>
  )
}

const Alert = ({ alert }: { alert: AdAlertListCardAlertFragment }) => {
  const getTaskTypeTranslation = useTaskTypeTranslation()
  const timeRemaining = useCountUpSince({ since: new Date(alert.createdAt) })

  return (
    <div
      css={theme => ({
        display: 'flex',
        alignItems: 'flex-start',
        gap: theme.space.Space2,
      })}
    >
      {getAlertIcon(alert)}
      <div
        css={{
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Text css={{ fontWeight: 'bold' }} size="lg">
          {getTaskTypeTranslation(alert.type)}
        </Text>
        {timeRemaining && (
          <Text
            size="sm"
            css={theme => ({
              color: theme.semantic.ColorContentReduced,
            })}
          >
            {timeRemaining}
          </Text>
        )}
      </div>
    </div>
  )
}
